import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Flame, Zap, BookOpen, AlarmClock, Camera, MessageCircle, Mic, Swords, Brain, TrendingUp, Sparkles, ArrowRight, Shield, GraduationCap } from "lucide-react";import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import OnboardingTour from "@/components/OnboardingTour";
import { langDisplayName } from "@/lib/languages";
import { useI18n } from "@/context/I18nContext";
import { ChangeLevelLink } from "@/components/LevelDialog";
import SchoolWorkCard from "@/components/SchoolWorkCard";

const QUICK = [
  { to: "/app", label: "LanguageLoop", icon: Camera, bg: "bg-mint" },
  { to: "/roommate", label: "Roommate", icon: MessageCircle, bg: "bg-lavender" },
  { to: "/accent", label: "AccentCoach", icon: Mic, bg: "bg-peach" },
  { to: "/debate", label: "DebateGym", icon: Swords, bg: "bg-sky" },
  { to: "/quiz", label: "QuizForge", icon: Brain, bg: "bg-butter" },
  { to: "/story", label: "Story", icon: BookOpen, bg: "bg-mint" }, // labels resolve via nav.* keys
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState(null);

  // Mount-only: load dashboard once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/dashboard").then((r) => {
      setData(r.data);
      const f = r.data.last_freeze_applied;
      const today = new Date().toISOString().slice(0, 10);
      if (f?.date === today && localStorage.getItem("fluent_freeze_ack") !== today) {
        localStorage.setItem("fluent_freeze_ack", today);
        toast.success(t("dash.freezeSaved", { count: r.data.streak_count }), { duration: 6000 });
      }
    }).catch((e) => console.warn("dashboard load failed", e));
    api.get("/my-assignments").then((r) => {
      setAssignments(r.data);
    }).catch(() => {});
  }, []);

  const { t, locale } = useI18n();
  const d = data || {};
  const first = user?.name?.split(" ")[0];
  return (
    <AppLayout>
      <OnboardingTour />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-cabinet font-extrabold text-4xl">{first ? t("dash.hey", { name: first }) : t("dash.heyAnon")}</h1>
            <p className="text-gray-600" data-testid="dash-learning">{t("dash.learning", { lang: langDisplayName(d.target_lang, locale), level: d.level })}<ChangeLevelLink onSaved={(level) => setData((x) => ({ ...x, level }))} /></p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/recap" className="nb-btn-outline px-5 py-2 text-sm" data-testid="dash-recap-link"><Sparkles className="w-4 h-4" /> {t("dash.recap")}</Link>
            {!d.has_pro_access && (
              <Link to="/billing" className="nb-btn-secondary px-5 py-2 text-sm">{t("dash.upgrade")}</Link>
            )}
          </div>
        </div>

        <Link to="/today" className="block mb-6" data-testid="dash-today-cta">
          <div className="nb-card bg-coral text-white p-6 flex items-center justify-between hover:shadow-hard-lg transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl border-2 border-ink bg-white flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-coral" />
              </div>
              <div>
                <div className="font-cabinet font-extrabold text-2xl">{t("dash.todayLoop")}</div>
                <div className="text-sm text-white/90">{t("dash.todayLoopSub")}</div>
              </div>
            </div>
            <ArrowRight className="w-7 h-7 shrink-0 rtl:rotate-180" />
          </div>
        </Link>

        <SchoolWorkCard data={assignments} />

        {/* Bento grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="nb-card bg-butter p-6 col-span-2 row-span-2 flex flex-col justify-between">
            <div>
              <div className="nb-tag bg-white">{t("dash.momentum")}</div>
              <div className="flex items-center gap-3 mt-4">
                <Flame className="w-12 h-12" />
                <div>
                  <div className="font-cabinet font-extrabold text-6xl leading-none" data-testid="dash-streak">{d.streak_count ?? 0}</div>
                  <div className="font-bold">{t("today.dayStreak", { count: d.streak_count ?? 0 })}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-4" data-testid="dash-freeze-tokens" title={t("dash.freezeHelp")}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <span key={i} className={`w-7 h-7 rounded-lg border-2 border-ink flex items-center justify-center ${i < (d.freeze_tokens ?? 0) ? "bg-sky" : "bg-white/40"}`}>
                    <Shield className={`w-4 h-4 ${i < (d.freeze_tokens ?? 0) ? "" : "opacity-30"}`} />
                  </span>
                ))}
                <span className="text-xs font-bold ml-1">{t("dash.freezes", { count: d.freeze_tokens ?? 0 })}</span>
              </div>
              <Link to="/streak" className="inline-block mt-3 text-xs font-bold underline hover:text-coral" data-testid="dash-streak-link">{t("dash.streakCalendar")}</Link>
            </div>
            <div className="flex items-center gap-3 mt-6 bg-white border-2 border-ink rounded-xl p-4">
              <Zap className="w-8 h-8" />
              <div><div className="font-cabinet font-extrabold text-3xl" data-testid="dash-xp">{d.xp ?? 0}</div><div className="text-xs font-bold">{t("dash.totalXp")}</div></div>
            </div>
          </motion.div>

          <Stat icon={BookOpen} bg="bg-mint" value={d.words_learned ?? 0} label={t("dash.wordsLearned")} testid="dash-words" />
          <Link to="/quiz" className="block">
            <div className="nb-card bg-coral text-white p-6 h-full" data-testid="dash-due">
              <AlarmClock className="w-7 h-7" />
              <div className="font-cabinet font-extrabold text-5xl mt-3">{d.due_for_review ?? 0}</div>
              <div className="font-bold text-sm">{t("dash.dueForReview")}</div>
            </div>
          </Link>
          <Stat icon={Camera} bg="bg-sky" value={d.captures ?? 0} label={t("dash.captures")} />
          <Stat icon={MessageCircle} bg="bg-lavender" value={d.conversations ?? 0} label={t("dash.conversations")} />
        </div>

        {/* Accent trend */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="nb-card p-6 lg:col-span-2">
            <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5" /><h3 className="font-cabinet font-bold text-xl">{t("dash.accentTrend")}</h3></div>
            {d.accent_trend?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={d.accent_trend} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#FF6B6B" strokeWidth={3} dot={{ r: 4, fill: "#0A0A0A" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex flex-col items-center justify-center text-gray-400">
                <Mic className="w-10 h-10" />
                <p className="mt-2 text-sm">{t("dash.noScores")} <Link to="/accent" className="text-coral font-bold">AccentCoach</Link>{t("dash.noScoresEnd")}</p>
              </div>
            )}
          </div>
          <div className="nb-card p-6">
            <h3 className="font-cabinet font-bold text-xl">{t("dash.recent")}</h3>
            <div className="space-y-3 mt-4 max-h-[220px] overflow-auto no-scrollbar">
              {d.recent_activity?.length ? d.recent_activity.map((a) => (
                <div key={`${a.created_at}-${a.mode}`} className="flex items-center gap-2 text-sm">
                  <span className="nb-tag bg-paper text-[10px]">{a.bundle}</span>
                  <span className="text-gray-600 truncate min-w-0">{a.summary}</span>
                </div>
              )) : <p className="text-sm text-gray-400">{t("dash.nothingYet")}</p>}
            </div>
          </div>
        </div>

        {/* Quick launch */}
        <h3 className="font-cabinet font-bold text-2xl mt-10 mb-4">{t("dash.jumpBack")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} className={`nb-card ${q.bg} p-5 hover:-translate-y-1 transition-transform`} data-testid={`quick-${q.label.toLowerCase()}`}>
              <q.icon className="w-7 h-7 stroke-[2.5]" />
              <div className="font-cabinet font-bold mt-3">{t(`nav.${q.label.toLowerCase()}`)}</div>
            </Link>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function Stat({ icon: Icon, bg, value, label, testid }) {
  return (
    <div className={`nb-card ${bg} p-6`} data-testid={testid}>
      <Icon className="w-7 h-7" />
      <div className="font-cabinet font-extrabold text-5xl mt-3">{value}</div>
      <div className="font-bold text-sm">{label}</div>
    </div>
  );
}
